import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import type {
  ChargePointWrapper,
  ChargePointCluster,
  ChargePointData,
} from '../types/charge-point.types';
import { useFilterStore } from '@/features/filters/store/filter-store';

// Enable Immer support for Map and Set
enableMapSet();

// Cache limits to prevent memory leaks
const MAX_CHARGE_POINT_CACHE_SIZE = 5000;
const MAX_CLUSTER_CACHE_SIZE = 1000;
const MAX_GROUP_MEMBERS_CACHE_SIZE = 100;

export interface ChargePointsState {
  // State
  singleChargePoints: ChargePointWrapper[];
  clusterChargePoints: ChargePointCluster[];

  // Local cache - Maps for fast lookups
  chargePointCache: Map<string, ChargePointWrapper>;
  clusterCache: Map<string, ChargePointCluster>;
  groupMembersCache: Map<number, ChargePointData[]>; // Cache group members by group ID

  selectedChargePoint: ChargePointWrapper | null;
  selectedGroupMembers: ChargePointData[];
  parentGroup: ChargePointWrapper | null; // Track parent group for back navigation
  overlapGroup: (ChargePointWrapper | ChargePointCluster)[]; // Overlapping markers at same location
  currentOverlapIndex: number; // Current index in overlap group
  openPinId: string | null;
  isDetailsPanelOpen: boolean;

  // Loading states
  isLoadingMap: boolean;
  isLoadingDetails: boolean;
  isLoadingGroup: boolean;

  // URL params
  urlChargePointId?: number;
  urlChargePointSiteId?: number;

  // Actions
  setChargePoints: (
    single: ChargePointWrapper[],
    clusters: ChargePointCluster[]
  ) => void;
  mergeChargePoints: (
    single: ChargePointWrapper[],
    clusters: ChargePointCluster[]
  ) => void;
  selectChargePoint: (cp: ChargePointWrapper | null) => void;
  setSelectedGroupMembers: (
    members: ChargePointData[],
    groupId?: number
  ) => void;
  getCachedGroupMembers: (groupId: number) => ChargePointData[] | undefined;
  setParentGroup: (group: ChargePointWrapper | null) => void;
  setOverlapGroup: (group: (ChargePointWrapper | ChargePointCluster)[]) => void;
  selectNextOverlap: () => void;
  selectPreviousOverlap: () => void;
  closeDetails: () => void;
  setOpenPinId: (id: string | null) => void;
  setLoadingMap: (loading: boolean) => void;
  setLoadingDetails: (loading: boolean) => void;
  setLoadingGroup: (loading: boolean) => void;
  setUrlChargePointId: (id?: number) => void;
  setUrlChargePointSiteId: (id?: number) => void;
  clearCache: () => void;
}

export const useChargePointsStore = create<ChargePointsState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      singleChargePoints: [],
      clusterChargePoints: [],

      // Initialize caches
      chargePointCache: new Map(),
      clusterCache: new Map(),
      groupMembersCache: new Map(),

      selectedChargePoint: null,
      selectedGroupMembers: [],
      parentGroup: null,
      overlapGroup: [],
      currentOverlapIndex: 0,
      openPinId: null,
      isDetailsPanelOpen: false,

      isLoadingMap: false,
      isLoadingDetails: false,
      isLoadingGroup: false,

      urlChargePointId: undefined,
      urlChargePointSiteId: undefined,

      // Set charge points from API (replaces existing data)
      setChargePoints: (
        single: ChargePointWrapper[],
        clusters: ChargePointCluster[]
      ) => {
        set({
          singleChargePoints: single,
          clusterChargePoints: clusters,
        });
      },

      // Merge charge points into cache (keeps existing pins visible)
      mergeChargePoints: (
        single: ChargePointWrapper[],
        clusters: ChargePointCluster[]
      ) => {
        set((state) => {
          // Merge single charge points into cache
          single.forEach((cp) => {
            state.chargePointCache.set(cp.id, cp);
          });

          // Merge clusters into cache
          clusters.forEach((cluster) => {
            state.clusterCache.set(cluster.id, cluster);
          });

          // Enforce cache size limits to prevent memory leaks
          if (state.chargePointCache.size > MAX_CHARGE_POINT_CACHE_SIZE) {
            // Remove oldest entries (first entries in Map)
            const entriesToRemove =
              state.chargePointCache.size - MAX_CHARGE_POINT_CACHE_SIZE;
            let removed = 0;
            for (const key of state.chargePointCache.keys()) {
              if (removed >= entriesToRemove) break;
              state.chargePointCache.delete(key);
              removed++;
            }
          }

          if (state.clusterCache.size > MAX_CLUSTER_CACHE_SIZE) {
            const entriesToRemove =
              state.clusterCache.size - MAX_CLUSTER_CACHE_SIZE;
            let removed = 0;
            for (const key of state.clusterCache.keys()) {
              if (removed >= entriesToRemove) break;
              state.clusterCache.delete(key);
              removed++;
            }
          }

          // Convert Maps back to arrays for rendering
          state.singleChargePoints = Array.from(
            state.chargePointCache.values()
          );
          state.clusterChargePoints = Array.from(state.clusterCache.values());
        });
      },

      // Select charge point
      selectChargePoint: (cp: ChargePointWrapper | null) => {
        const state = get();

        // Clear overlap group if selecting a new charge point that's not in the current overlap group
        if (cp && state.overlapGroup.length > 0) {
          const isInOverlapGroup = state.overlapGroup.some(
            (item) => item.id === cp.id
          );
          if (!isInOverlapGroup) {
            set({
              overlapGroup: [],
              currentOverlapIndex: 0,
            });
          }
        }

        set({
          selectedChargePoint: cp,
          isDetailsPanelOpen: !!cp,
          openPinId: cp?.id || null,
        });

        // Update URL with selected charge point
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);

          if (cp) {
            // Add charge point to URL
            if (cp.type === 'chargePoint' && cp.chargePoint) {
              url.searchParams.set(
                'chargePointId',
                cp.chargePoint.id.toString()
              );
              url.searchParams.delete('siteId');
            } else if (cp.type === 'chargePointGroup' && cp.chargePointGroup) {
              url.searchParams.set('siteId', cp.chargePointGroup.id.toString());
              url.searchParams.delete('chargePointId');
            }
          } else {
            // Remove charge point from URL
            url.searchParams.delete('chargePointId');
            url.searchParams.delete('siteId');
          }

          // Update URL without reload
          window.history.replaceState({}, '', url.toString());
        }

        // On mobile (<800px), close filter panel when opening charge point details
        // This prevents bottom sheets from overlapping
        if (cp && typeof window !== 'undefined') {
          const isMobile = window.innerWidth < 800;
          if (isMobile) {
            const isFilterOpen = useFilterStore.getState().isFilterPanelOpen;
            if (isFilterOpen) {
              useFilterStore.setState({ isFilterPanelOpen: false });
            }
          }
        }
      },

      // Set selected group members
      setSelectedGroupMembers: (
        members: ChargePointData[],
        groupId?: number
      ) => {
        set((state) => {
          state.selectedGroupMembers = members;
          // Cache the group members if groupId is provided
          if (groupId !== undefined) {
            state.groupMembersCache.set(groupId, members);

            // Enforce cache size limit
            if (state.groupMembersCache.size > MAX_GROUP_MEMBERS_CACHE_SIZE) {
              // Remove oldest entry
              const firstKey = state.groupMembersCache.keys().next().value;
              if (firstKey !== undefined) {
                state.groupMembersCache.delete(firstKey);
              }
            }
          }
        });
      },

      // Get cached group members
      getCachedGroupMembers: (groupId: number) => {
        const state = get();
        return state.groupMembersCache.get(groupId);
      },

      // Set parent group for back navigation
      setParentGroup: (group: ChargePointWrapper | null) => {
        set({ parentGroup: group });
      },

      // Set overlap group (markers at same location)
      setOverlapGroup: (group: (ChargePointWrapper | ChargePointCluster)[]) => {
        set({ overlapGroup: group, currentOverlapIndex: 0 });
      },

      // Navigate to next item in overlap group
      selectNextOverlap: () => {
        const state = get();
        if (state.overlapGroup.length === 0) return;

        const nextIndex =
          (state.currentOverlapIndex + 1) % state.overlapGroup.length;
        const nextItem = state.overlapGroup[nextIndex];

        if (!nextItem) return;

        set({ currentOverlapIndex: nextIndex });

        // Select the next item (could be cluster or charge point)
        if ('count' in nextItem) {
          // It's a cluster - for now, just update index
          // Clusters don't have details panel, so we just track the index
        } else {
          // It's a charge point - select it
          get().selectChargePoint(nextItem);
        }
      },

      // Navigate to previous item in overlap group
      selectPreviousOverlap: () => {
        const state = get();
        if (state.overlapGroup.length === 0) return;

        const prevIndex =
          state.currentOverlapIndex === 0
            ? state.overlapGroup.length - 1
            : state.currentOverlapIndex - 1;
        const prevItem = state.overlapGroup[prevIndex];

        if (!prevItem) return;

        set({ currentOverlapIndex: prevIndex });

        // Select the previous item
        if ('count' in prevItem) {
          // It's a cluster
        } else {
          // It's a charge point - select it
          get().selectChargePoint(prevItem);
        }
      },

      // Close details panel
      closeDetails: () => {
        set({
          selectedChargePoint: null,
          selectedGroupMembers: [],
          parentGroup: null,
          overlapGroup: [],
          currentOverlapIndex: 0,
          isDetailsPanelOpen: false,
          openPinId: null,
          // Nothing is shown any more, so nothing is loading for display. A
          // cancelled in-flight fetch could leave these stuck, which made the
          // URL writer keep (restore) the closed chargePointId/siteId.
          isLoadingDetails: false,
          isLoadingGroup: false,
          // The deep link (if any) is done with; don't treat it as pending
          urlChargePointId: undefined,
          urlChargePointSiteId: undefined,
        });

        // Remove charge point/site from URL (both new and legacy parameter names)
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete('chargePointId');
          url.searchParams.delete('siteId');
          url.searchParams.delete('cpId'); // Legacy parameter from useUrlParams
          url.searchParams.delete('cpSiteId'); // Legacy parameter from useUrlParams
          window.history.replaceState({}, '', url.toString());
        }
      },

      // Set open pin ID
      setOpenPinId: (id: string | null) => {
        set({ openPinId: id });
      },

      // Set loading states
      setLoadingMap: (loading: boolean) => {
        set({ isLoadingMap: loading });
      },

      setLoadingDetails: (loading: boolean) => {
        set({ isLoadingDetails: loading });
      },

      setLoadingGroup: (loading: boolean) => {
        set({ isLoadingGroup: loading });
      },

      // Set URL params
      setUrlChargePointId: (id?: number) => {
        set({ urlChargePointId: id });
      },

      setUrlChargePointSiteId: (id?: number) => {
        set({ urlChargePointSiteId: id });
      },

      // Clear all caches (useful for refresh/reset)
      clearCache: () => {
        set((state) => {
          state.chargePointCache.clear();
          state.clusterCache.clear();
          state.groupMembersCache.clear();
          state.singleChargePoints = [];
          state.clusterChargePoints = [];
        });
      },
    })),
    { name: 'ChargePointsStore' }
  )
);
